import { useEffect } from 'react';

export default function PoliticasPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0C] pt-24 pb-16" data-testid="policies-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <h1 className="text-3xl font-light tracking-tight text-zinc-100 mb-8 font-[Outfit]">
          Políticas de la Plataforma
        </h1>
        <p className="text-xs text-zinc-500 mb-8">Última actualización: Abril 2026</p>

        <div className="prose-legal space-y-10 text-sm text-zinc-400 leading-relaxed">

          {/* Especies Restringidas */}
          <section>
            <h2 className="text-xl text-zinc-100 mb-4 font-medium border-b border-white/5 pb-2">Política de Especies Restringidas</h2>

            <h3 className="text-base text-zinc-200 mb-2 font-medium">Clasificación de especies</h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li><strong className="text-emerald-400">Comunes:</strong> Publicación libre con declaración de legalidad. Incluye: bettas, geckos leopardo, dragones barbudos, corn snakes, peces de agua dulce comunes.</li>
              <li><strong className="text-amber-400">Documentación requerida:</strong> Reptiles, aves, mamíferos exóticos requieren factura, certificado de criadero u otro documento que acredite legal posesión.</li>
              <li><strong className="text-orange-400">Revisión manual:</strong> Especies listadas en NOM-059 o CITES requieren documentación obligatoria y aprobación del equipo de MorphMX antes de publicarse.</li>
              <li><strong className="text-red-400">Prohibidas:</strong> Especies cuya venta es ilegal en México. Listados bloqueados inmediatamente.</li>
            </ul>

            <h3 className="text-base text-zinc-200 mb-2 font-medium">NOM-059-SEMARNAT-2010</h3>
            <p>Las especies listadas en la NOM-059 como en Peligro de Extinción (P), Amenazadas (A) o Sujetas a Protección Especial (Pr) requieren documentación UMA o PIMVS válida. Sin documentación, el listado será rechazado.</p>

            <h3 className="text-base text-zinc-200 mb-2 font-medium mt-4">CITES</h3>
            <p>Las especies listadas en CITES Apéndice I, II o III requieren documentación correspondiente. El comercio de Apéndice I sin permisos específicos está prohibido en MorphMX.</p>
          </section>

          {/* Compra Protegida */}
          <section>
            <h2 className="text-xl text-zinc-100 mb-4 font-medium border-b border-white/5 pb-2">Política de Compra Protegida</h2>
            <p>MorphMX ofrece un servicio opcional de intermediación de pagos. El comprador paga a MorphMX, el vendedor envía el animal, el comprador confirma recepción y MorphMX libera el pago al vendedor menos la comisión de servicio (configurable entre 8% y 15%, default 10%).</p>
            <p className="mt-2">El pago se libera automáticamente 48 horas después de confirmada la entrega, o tras resolución administrativa en caso de disputa.</p>
          </section>

          {/* Subastas */}
          <section>
            <h2 className="text-xl text-zinc-100 mb-4 font-medium border-b border-white/5 pb-2">Política de Subastas</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>Las pujas son compromisos vinculantes.</li>
              <li>El ganador tiene un plazo definido para completar el pago.</li>
              <li>Si el ganador no paga, se puede ofrecer al siguiente postor.</li>
              <li>El shill bidding (pujar en tu propia subasta) está prohibido y resulta en suspensión.</li>
              <li>Anti-sniping: las pujas en los últimos minutos extienden la subasta.</li>
              <li>El vendedor puede cancelar una subasta sin pujas.</li>
            </ul>
          </section>

          {/* Reportes y Strikes */}
          <section>
            <h2 className="text-xl text-zinc-100 mb-4 font-medium border-b border-white/5 pb-2">Política de Reportes y Strikes</h2>
            <h3 className="text-base text-zinc-200 mb-2 font-medium">Sistema de Strikes</h3>
            <ul className="list-disc pl-5 space-y-2 mb-4">
              <li><strong className="text-amber-400">Strike 1:</strong> Advertencia formal y reducción de reputación.</li>
              <li><strong className="text-orange-400">Strike 2:</strong> Bloqueo temporal de compras y pujas.</li>
              <li><strong className="text-red-400">Strike 3:</strong> Suspensión de cuenta y revisión manual. Posible ban permanente.</li>
            </ul>
            <p>Los reportes requieren evidencia y son revisados por el equipo de MorphMX. Los reportes falsos o malintencionados también pueden resultar en strikes.</p>
          </section>

          {/* Reembolsos y Disputas */}
          <section>
            <h2 className="text-xl text-zinc-100 mb-4 font-medium border-b border-white/5 pb-2">Política de Reembolsos y Disputas</h2>
            <p>Las disputas se gestionan a través de Compra Protegida. En transacciones directas entre usuarios, MorphMX puede mediar pero no garantiza reembolsos. Las disputas se resuelven en base a:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Evidencia fotográfica y documental.</li>
              <li>Historial de comunicación entre las partes.</li>
              <li>Reputación y historial de ambas partes.</li>
              <li>Dictamen de MorphMX, que es vinculante dentro de la plataforma.</li>
            </ul>
            <p className="mt-2">Motivos de disputa válidos: no recibido, recibido incorrecto, problemas de salud, genética no coincide, fraude, retraso significativo.</p>
          </section>

          {/* Contenido Prohibido */}
          <section>
            <h2 className="text-xl text-zinc-100 mb-4 font-medium border-b border-white/5 pb-2">Política de Contenido Prohibido</h2>
            <p>Queda prohibido publicar en MorphMX:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Fauna protegida sin respaldo documental válido.</li>
              <li>Especies ilegales o de origen desconocido.</li>
              <li>Contenido que promueva el tráfico de vida silvestre.</li>
              <li>Listados engañosos (fotos falsas, descripciones incorrectas).</li>
              <li>Documentos falsos o alterados.</li>
              <li>Actividades de shill bidding o manipulación de subastas.</li>
              <li>Cualquier forma de fraude.</li>
              <li>Contenido ofensivo, discriminatorio o que viole derechos de terceros.</li>
            </ul>
            <p className="mt-2">La publicación de contenido prohibido resultará en la remoción inmediata del listado y posibles strikes o suspensión de cuenta.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
